import { TableRow, TableCell, IconButton, Box, Typography, Checkbox, Switch } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DisabledByDefaultOutlinedIcon from '@mui/icons-material/DisabledByDefaultOutlined';
import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import * as styles from './FoodTable.styles';
import { FoodItem, RECOMMENDATION_TYPE } from '../../userMenu.types';

interface DraggableRecommendationProps {
    recItem: FoodItem;
    index: number;
    moveRecommendation: (dragIndex: number, hoverIndex: number) => void;
    onActiveChange: (id: string, checked: boolean) => void;
    onRemove: (id: string) => void;
}

export function DraggableRecommendation({ recItem, index, moveRecommendation, onActiveChange, onRemove }: DraggableRecommendationProps) {
    const recRef = useRef<HTMLTableRowElement>(null);

    const isFoodDisabled = recItem.food_enabled === false;

    const [{ isDragging }, drag] = useDrag({
        type: RECOMMENDATION_TYPE,
        item: { index },
        canDrag: !isFoodDisabled,
        collect: (monitor) => ({
            isDragging: monitor.isDragging()
        })
    });

    const [, drop] = useDrop({
        accept: RECOMMENDATION_TYPE,
        hover: (draggedItem: { index: number }, monitor) => {
            if (!recRef.current) {
                return;
            }

            const dragIndex = draggedItem.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = recRef.current.getBoundingClientRect();

            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            const clientOffset = monitor.getClientOffset();

            if (!clientOffset) {
                return;
            }

            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveRecommendation(dragIndex, hoverIndex);

            draggedItem.index = hoverIndex;
        }
    });

    drag(drop(recRef));

    return (
        <TableRow
            ref={recRef}
            sx={{
                ...styles.recommendationRowStyles,
                bgcolor: isFoodDisabled ? '#F2F4F7' : !recItem.enabled ? '#e0e0e0' : '#fafafa',
                opacity: isDragging ? 0.5 : 1,
                cursor: isFoodDisabled ? 'not-allowed' : isDragging ? 'grabbing' : 'default'
            }}
        >
            <TableCell>
            </TableCell>

            <TableCell>
                <Box sx={styles.dragHandleBoxStyles}>
                    <div style={{ display: 'flex', cursor: isFoodDisabled ? 'not-allowed' : 'grab' }}>
                        <IconButton
                            size="small"
                            sx={styles.dragIconButtonStyles}
                            disabled={isFoodDisabled}
                        >
                            <MenuIcon sx={styles.menuIconStyles} />
                        </IconButton>
                    </div>
                </Box>
            </TableCell>

            <TableCell>
                <Box sx={styles.foodBoxStyles}>
                    <Box
                        component="img"
                        src={recItem.image}
                        alt={recItem.name}
                        sx={styles.foodImageStyles}
                    />
                    <Box>
                        <Typography sx={styles.foodNameStyles}>
                            {recItem.name}
                        </Typography>
                        <Typography sx={styles.foodStoreStyles}>
                            {recItem.store}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>

            <TableCell sx={styles.priceCellStyles}>
                <Box sx={styles.priceBoxStyles}>
                    <Typography sx={styles.priceTypographyStyles}>
                        ¥{recItem.price.toLocaleString()}
                    </Typography>
                </Box>
            </TableCell>
            <TableCell>
            </TableCell>

            <TableCell onClick={(e) => e.stopPropagation()}>
                {!isFoodDisabled && <Switch
                    checked={recItem.enabled}
                    onChange={(e) => onActiveChange(recItem.id, e.target.checked)}
                    sx={styles.switchStyles}
                />}
            </TableCell>

            <TableCell onClick={(e) => e.stopPropagation()}>
                <IconButton
                    size="small"
                    disabled={isFoodDisabled}
                    onClick={() => !isFoodDisabled && onRemove(recItem.id)}
                    sx={styles.editButtonStyles}
                >
                    <DisabledByDefaultOutlinedIcon sx={styles.editIconStyles} />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}
